import { z } from "zod";
import { contrastRatio } from "../scene/color";
import {
  BRAND_RADIUS_KEYS,
  type BrandRadiusKey,
  brandTokenKeySchema,
  cmykSchema,
  type Swatch,
} from "../scene/schema";

/**
 * Brand kit tokens (docs/06-brand-kit.md). Stored as JSON in `brand_kit.tokens`
 * and referenced from documents by key (`brandBindingSchema`).
 */

/** Sizes, spacings and radii refer to a 1080 px wide format and scale with it. */
export const BRAND_REFERENCE_WIDTH = 1080;

export const BRAND_COLOR_ROLES = [
  "primary",
  "secondary",
  "accent",
  "background",
  "surface",
  "text",
  "text-muted",
] as const;
export type BrandColorRole = (typeof BRAND_COLOR_ROLES)[number];

export const BRAND_COLOR_ROLE_LABELS: Record<BrandColorRole, string> = {
  primary: "Primärfarbe",
  secondary: "Sekundärfarbe",
  accent: "Akzent",
  background: "Hintergrund",
  surface: "Fläche",
  text: "Text",
  "text-muted": "Text gedämpft",
};

export const BRAND_TEXT_STYLE_KEYS = [
  "heading-1",
  "heading-2",
  "subline",
  "body",
  "caption",
  "quote",
] as const;
export type BrandTextStyleKey = (typeof BRAND_TEXT_STYLE_KEYS)[number];

export const BRAND_SPACING_KEYS = ["xs", "sm", "md", "lg", "xl"] as const;
export type BrandSpacingKey = (typeof BRAND_SPACING_KEYS)[number];

export const BRAND_RADIUS_LABELS: Record<BrandRadiusKey, string> = {
  none: "Eckig",
  sm: "Klein",
  md: "Mittel",
  lg: "Groß",
  full: "Rund",
};

export const BRAND_LOGO_VARIANTS = ["primary", "light", "dark", "icon"] as const;
export type BrandLogoVariant = (typeof BRAND_LOGO_VARIANTS)[number];

export const BRAND_LOGO_LABELS: Record<BrandLogoVariant, string> = {
  primary: "Hauptlogo",
  light: "Hell (für dunkle Hintergründe)",
  dark: "Dunkel (für helle Hintergründe)",
  icon: "Signet / Icon",
};

const opaqueHexSchema = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/, "Farbe als Hex-Wert, z. B. #1a2b3c")
  .transform((value) => value.toLowerCase());

export const brandColorSchema = z.object({
  key: brandTokenKeySchema,
  name: z.string().trim().min(1, "Bitte einen Namen angeben").max(60),
  color: opaqueHexSchema,
  /** Exact CMYK value from the brand's style guide (lands unchanged in print PDFs). */
  cmyk: cmykSchema.nullable().default(null),
  /** Informational, e.g. "Pantone 2728 C". */
  pantone: z.string().trim().max(40).nullable().default(null),
});

export const brandTextStyleSchema = z.object({
  key: brandTokenKeySchema,
  name: z.string().trim().min(1, "Bitte einen Namen angeben").max(60),
  fontFamily: z.string().min(1).max(100),
  fontWeight: z.number().int().min(100).max(900),
  italic: z.boolean().default(false),
  /** Font size in px on a 1080 px wide format. */
  fontSize: z.number().min(1).max(1000),
  lineHeight: z.number().min(0.5).max(5),
  /** In em. */
  letterSpacing: z.number().min(-1).max(2).default(0),
  uppercase: z.boolean().default(false),
  /** Default colour (colour token key) applied together with the style. */
  color: brandTokenKeySchema.nullable().default(null),
});

const sizeSchema = z.number().min(0).max(1000);

export const brandTokensSchema = z
  .object({
    colors: z.array(brandColorSchema).max(60),
    textStyles: z.array(brandTextStyleSchema).max(30),
    spacing: z.object({
      xs: sizeSchema,
      sm: sizeSchema,
      md: sizeSchema,
      lg: sizeSchema,
      xl: sizeSchema,
    }),
    radius: z.object({ sm: sizeSchema, md: sizeSchema, lg: sizeSchema }),
    /** Default print settings for new print documents. */
    print: z.object({
      provider: z.string().max(50).nullable().default(null),
      paper: z.enum(["coated", "uncoated"]).nullable().default(null),
      profile: z.string().max(50).nullable().default(null),
    }),
  })
  .superRefine((tokens, ctx) => {
    const unique = (keys: string[], path: string) => {
      const seen = new Set<string>();
      for (const [index, key] of keys.entries()) {
        if (seen.has(key)) {
          ctx.addIssue({
            code: "custom",
            path: [path, index, "key"],
            message: `Der Schlüssel „${key}“ kommt mehrfach vor`,
          });
        }
        seen.add(key);
      }
    };
    unique(
      tokens.colors.map((color) => color.key),
      "colors",
    );
    unique(
      tokens.textStyles.map((style) => style.key),
      "textStyles",
    );
  });

export type BrandColor = z.infer<typeof brandColorSchema>;
export type BrandTextStyle = z.infer<typeof brandTextStyleSchema>;
export type BrandTokens = z.infer<typeof brandTokensSchema>;

export const DEFAULT_BRAND_TOKENS: BrandTokens = {
  colors: [
    { key: "primary", name: "Primärfarbe", color: "#2563eb", cmyk: null, pantone: null },
    { key: "secondary", name: "Sekundärfarbe", color: "#0f172a", cmyk: null, pantone: null },
    { key: "accent", name: "Akzent", color: "#f59e0b", cmyk: null, pantone: null },
    { key: "background", name: "Hintergrund", color: "#ffffff", cmyk: null, pantone: null },
    { key: "surface", name: "Fläche", color: "#f4f4f5", cmyk: null, pantone: null },
    { key: "text", name: "Text", color: "#111827", cmyk: null, pantone: null },
    { key: "text-muted", name: "Text gedämpft", color: "#6b7280", cmyk: null, pantone: null },
  ],
  textStyles: [
    {
      key: "heading-1",
      name: "Überschrift 1",
      fontFamily: "Inter",
      fontWeight: 800,
      italic: false,
      fontSize: 88,
      lineHeight: 1.05,
      letterSpacing: -0.02,
      uppercase: false,
      color: "text",
    },
    {
      key: "heading-2",
      name: "Überschrift 2",
      fontFamily: "Inter",
      fontWeight: 700,
      italic: false,
      fontSize: 64,
      lineHeight: 1.1,
      letterSpacing: -0.01,
      uppercase: false,
      color: "text",
    },
    {
      key: "subline",
      name: "Unterzeile",
      fontFamily: "Inter",
      fontWeight: 600,
      italic: false,
      fontSize: 44,
      lineHeight: 1.2,
      letterSpacing: 0,
      uppercase: false,
      color: "text",
    },
    {
      key: "body",
      name: "Fließtext",
      fontFamily: "Inter",
      fontWeight: 400,
      italic: false,
      fontSize: 32,
      lineHeight: 1.4,
      letterSpacing: 0,
      uppercase: false,
      color: "text",
    },
    {
      key: "caption",
      name: "Bildunterschrift",
      fontFamily: "Inter",
      fontWeight: 500,
      italic: false,
      fontSize: 24,
      lineHeight: 1.3,
      letterSpacing: 0.02,
      uppercase: false,
      color: "text-muted",
    },
    {
      key: "quote",
      name: "Zitat",
      fontFamily: "Playfair Display",
      fontWeight: 400,
      italic: true,
      fontSize: 48,
      lineHeight: 1.25,
      letterSpacing: 0,
      uppercase: false,
      color: "text",
    },
  ],
  spacing: { xs: 8, sm: 16, md: 24, lg: 40, xl: 64 },
  radius: { sm: 8, md: 16, lg: 32 },
  print: { provider: null, paper: null, profile: null },
};

/**
 * Reads stored tokens leniently: invalid or missing parts fall back to the
 * defaults, and the fixed colour roles and text styles always exist.
 */
export function normalizeBrandTokens(raw: unknown): BrandTokens {
  const input = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const pick = <T>(schema: z.ZodType<T>, value: unknown, fallback: T): T => {
    const parsed = schema.safeParse(value);
    return parsed.success ? parsed.data : fallback;
  };
  const colors = pick(z.array(brandColorSchema).max(60), input.colors, DEFAULT_BRAND_TOKENS.colors);
  const textStyles = pick(
    z.array(brandTextStyleSchema).max(30),
    input.textStyles,
    DEFAULT_BRAND_TOKENS.textStyles,
  );
  const dedupe = <T extends { key: string }>(items: T[]) =>
    items.filter((item, index) => items.findIndex((other) => other.key === item.key) === index);
  const withRequired = <T extends { key: string }>(items: T[], required: readonly T[]) => {
    const result = dedupe(items);
    for (const item of required) {
      if (!result.some((existing) => existing.key === item.key)) result.push(item);
    }
    return result;
  };
  return {
    colors: withRequired(colors, DEFAULT_BRAND_TOKENS.colors),
    textStyles: withRequired(textStyles, DEFAULT_BRAND_TOKENS.textStyles),
    spacing: pick(brandTokensSchema.shape.spacing, input.spacing, DEFAULT_BRAND_TOKENS.spacing),
    radius: pick(brandTokensSchema.shape.radius, input.radius, DEFAULT_BRAND_TOKENS.radius),
    print: pick(brandTokensSchema.shape.print, input.print, DEFAULT_BRAND_TOKENS.print),
  };
}

export function isRequiredColor(key: string): boolean {
  return (BRAND_COLOR_ROLES as readonly string[]).includes(key);
}

export function isRequiredTextStyle(key: string): boolean {
  return (BRAND_TEXT_STYLE_KEYS as readonly string[]).includes(key);
}

/** Next free key for a new free colour or text style ("farbe-1", "stil-2" …). */
export function nextTokenKey(existing: readonly { key: string }[], prefix: string): string {
  for (let index = 1; ; index++) {
    const key = `${prefix}-${index}`;
    if (!existing.some((item) => item.key === key)) return key;
  }
}

export function brandColor(tokens: BrandTokens, key: string): BrandColor | undefined {
  return tokens.colors.find((color) => color.key === key);
}

export function brandTextStyle(tokens: BrandTokens, key: string): BrandTextStyle | undefined {
  return tokens.textStyles.find((style) => style.key === key);
}

/** Radius in px on a 1080 px wide format. */
export function brandRadius(tokens: BrandTokens, key: BrandRadiusKey): number {
  if (key === "none") return 0;
  if (key === "full") return 9999;
  return tokens.radius[key];
}

export { BRAND_RADIUS_KEYS };

/** Colours with exact CMYK values, used as print swatches for every document of the kit. */
export function brandSwatches(tokens: BrandTokens): Swatch[] {
  return tokens.colors.flatMap((color) =>
    color.cmyk ? [{ color: color.color, cmyk: color.cmyk, name: color.name }] : [],
  );
}

export interface ContrastCheck {
  foreground: string;
  background: string;
  ratio: number;
  /** WCAG level for normal text: AAA ≥ 7, AA ≥ 4.5, "AA groß" ≥ 3 (large text only). */
  level: "AAA" | "AA" | "AA groß" | "ungenügend";
}

export function contrastLevel(ratio: number): ContrastCheck["level"] {
  if (ratio >= 7) return "AAA";
  if (ratio >= 4.5) return "AA";
  if (ratio >= 3) return "AA groß";
  return "ungenügend";
}

/** WCAG contrast of the typical text/background pairs of a kit. */
export function brandContrastChecks(tokens: BrandTokens): ContrastCheck[] {
  const pairs: [string, string][] = [
    ["text", "background"],
    ["text-muted", "background"],
    ["text", "surface"],
    ["background", "primary"],
    ["background", "secondary"],
  ];
  return pairs.flatMap(([fg, bg]) => {
    const foreground = brandColor(tokens, fg);
    const background = brandColor(tokens, bg);
    if (!foreground || !background) return [];
    const ratio = contrastRatio(foreground.color, background.color);
    return [{ foreground: fg, background: bg, ratio, level: contrastLevel(ratio) }];
  });
}
